(() => {
  const SELECTOR = '[data-hp-coming-soon]';

  const asNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const asBoolean = (value, fallback = false) => {
    if (value === undefined || value === null || value === '') return fallback;
    return ['true', '1', 'yes'].includes(String(value).toLowerCase());
  };

  const loadScript = (url) =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${url}"]`);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        if (existing.dataset.loaded === 'true') resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

  const loadStyle = (url) => {
    if (document.querySelector(`link[href="${url}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  };

  const initDynamicSwiper = async (root) => {
    const swiperEl = root.querySelector('[data-hp-swiper]');
    if (!swiperEl) return;

    const alreadyMounted = swiperEl.dataset.hpSwiperMounted === 'true';
    if (alreadyMounted) return;

    if (!window.Swiper) {
      loadStyle('https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css');
      try {
        await loadScript('https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js');
      } catch (error) {
        console.warn('[hp-coming-soon] Could not load Swiper.', error);
        return;
      }
    }

    const autoplayEnabled = asBoolean(swiperEl.dataset.swiperAutoplay, true);
    const settings = {
      slidesPerView: asNumber(swiperEl.dataset.swiperSlidesPerView, 1),
      spaceBetween: asNumber(swiperEl.dataset.swiperSpaceBetween, 0),
      speed: asNumber(swiperEl.dataset.swiperSpeed, 700),
      loop: asBoolean(swiperEl.dataset.swiperLoop, true),
      effect: swiperEl.dataset.swiperEffect || 'slide',
      pagination: {
        el: swiperEl.querySelector('.swiper-pagination'),
        clickable: true,
      },
    };

    if (autoplayEnabled) {
      settings.autoplay = {
        delay: asNumber(swiperEl.dataset.swiperAutoplayDelay, 2200),
        disableOnInteraction: false,
      };
    }

    // eslint-disable-next-line no-new
    new window.Swiper(swiperEl, settings);
    swiperEl.dataset.hpSwiperMounted = 'true';
  };

  const revealContent = (root) => {
    const loader = root.querySelector('[data-hp-loader]');
    const content = root.querySelector('[data-hp-content]');
    const swiper = root.querySelector('[data-hp-swiper]');

    if (!loader || !content) return;

    const hasGsap = typeof window.gsap !== 'undefined';
    const hasScrollTrigger = typeof window.ScrollTrigger !== 'undefined';

    if (hasGsap) {
      const timeline = window.gsap.timeline();
      timeline
        .to(loader, { autoAlpha: 0, duration: 0.35, ease: 'power1.out' })
        .set(loader, { display: 'none' })
        .to(content, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power1.out' }, '-=0.1');

      if (swiper) {
        timeline.to(swiper, { autoAlpha: 1, y: 0, duration: 0.35 }, '-=0.25');
      }

      if (hasScrollTrigger) {
        window.ScrollTrigger.refresh();
      }
    } else {
      loader.style.display = 'none';
      content.style.opacity = '1';
      if (swiper) swiper.style.opacity = '1';
    }

    if (window.lenis && typeof window.lenis.resize === 'function') {
      window.lenis.resize();
    }
  };

  const initLoadingAnimation = (root) => {
    if (root.dataset.hpInit === 'true') return;
    root.dataset.hpInit = 'true';

    const countEl = root.querySelector('[data-hp-count]');
    if (!countEl) return;

    const loadDuration = asNumber(root.dataset.hpLoadDuration, 1400);
    const start = performance.now();

    const frame = (now) => {
      const progress = Math.min((now - start) / loadDuration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(eased * 100);
      countEl.textContent = String(value).padStart(2, '0');

      if (progress < 1) {
        window.requestAnimationFrame(frame);
      } else {
        revealContent(root);
      }
    };

    window.requestAnimationFrame(frame);
  };

  const init = async () => {
    const wrappers = document.querySelectorAll(SELECTOR);
    if (!wrappers.length) return;

    await Promise.all(Array.from(wrappers, (wrapper) => initDynamicSwiper(wrapper)));
    wrappers.forEach((wrapper) => initLoadingAnimation(wrapper));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
